import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, isValid, parseISO } from 'date-fns';
import { Calendar, Activity, Download, Loader2, ChevronRight, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';
import { useBusinessUnitPnL, BUViewPeriod, type ExpenseDetail, type ChipChipPnlRow } from '@/hooks/finance/useBusinessUnitPnL';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';
import { formatCurrency } from '@/lib/currency';

type DisplayCurrency = 'ETB' | 'USD';

// Drill-down table for a line's underlying records (opex or grant inflows).
const DetailPanel: React.FC<{
  details: ExpenseDetail[];
  currency: DisplayCurrency;
  fmt: (n: number) => string;
}> = ({ details, currency, fmt }) => (
  <div className="px-8 py-3">
    <table className="w-full text-[11px]">
      <thead>
        <tr className="text-slate-500 uppercase text-[9px] tracking-wider">
          <th className="text-left py-1 pr-3 font-semibold">Date</th>
          <th className="text-left py-1 pr-3 font-semibold">Source / Vendor</th>
          <th className="text-left py-1 pr-3 font-semibold">Description</th>
          <th className="text-left py-1 pr-3 font-semibold">Cost Position</th>
          <th className="text-right py-1 pr-3 font-semibold">Original</th>
          <th className="text-right py-1 pr-3 font-semibold">%</th>
          <th className="text-right py-1 font-semibold">{currency} Portion</th>
        </tr>
      </thead>
      <tbody>
        {details
          .slice()
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .map((d, idx) => (
            <tr key={`${d.id}-${idx}`} className="border-t border-slate-200/70 hover:bg-white/60">
              <td className="py-1 pr-3 text-slate-600 whitespace-nowrap">{format(parseISO(d.date), 'MMM d, yyyy')}</td>
              <td className="py-1 pr-3 text-slate-700 truncate max-w-[180px]">{d.vendor || '—'}</td>
              <td className="py-1 pr-3 text-slate-500 truncate max-w-[260px]">{d.description || '—'}</td>
              <td className="py-1 pr-3 text-slate-500 truncate max-w-[180px]">{d.costPosition || d.reference || '—'}</td>
              <td className="py-1 pr-3 text-right tabular-nums text-slate-600">{formatCurrency(d.originalAmount, d.currency)}</td>
              <td className="py-1 pr-3 text-right tabular-nums text-slate-500">{d.pct}%</td>
              <td className="py-1 text-right tabular-nums font-semibold text-slate-800">{fmt(d.portion)}</td>
            </tr>
          ))}
      </tbody>
    </table>
  </div>
);

// ChipChip's accrual-P&L layout (CP1/CP2/COGS/SG&A) rendered in place of the
// flat Sales/OpEx list. COGS & SG&A lines are collapsible to their records.
const ChipChipPnlRows: React.FC<{
  rows: ChipChipPnlRow[];
  buValue: string;
  periodKeys: string[];
  expanded: Record<string, boolean>;
  toggleRow: (k: string) => void;
  currency: DisplayCurrency;
  fmt: (n: number) => string;
}> = ({ rows, buValue, periodKeys, expanded, toggleRow, currency, fmt }) => {
  const total = (v: Record<string, number>) => periodKeys.reduce((s, k) => s + (v[k] || 0), 0);
  const pctFmt = (r: number) => (Number.isFinite(r) ? `${(r * 100).toFixed(1)}%` : '–');
  return (
    <>
      {rows.map((row) => {
        if (row.kind === 'header') {
          return (
            <tr key={row.key} className="bg-slate-100/70 border-y border-slate-200">
              <td className="sticky left-0 z-10 bg-slate-100/70 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 border-r border-slate-200">
                {row.label}
              </td>
              {periodKeys.map((k) => (
                <td key={k} className="px-4 py-1.5" />
              ))}
              <td className="px-4 py-1.5 border-l border-slate-200" />
            </tr>
          );
        }
        if (row.kind === 'sub') {
          const t = total(row.values);
          return (
            <tr key={row.key} className="hover:bg-slate-50/50 text-muted-foreground">
              <td className="sticky left-0 z-10 bg-white pl-8 pr-4 py-1.5 text-xs border-r border-slate-100">{row.label}</td>
              {periodKeys.map((k) => (
                <td key={k} className="px-4 py-1.5 text-right tabular-nums text-xs">{row.values[k] ? fmt(row.values[k]) : '–'}</td>
              ))}
              <td className="px-4 py-1.5 text-right tabular-nums text-xs border-l border-slate-100">{t ? fmt(t) : '–'}</td>
            </tr>
          );
        }
        if (row.kind === 'margin') {
          return (
            <tr key={row.key} className="text-emerald-700 italic">
              <td className="sticky left-0 z-10 bg-white pl-8 pr-4 py-1 text-[11px] border-r border-slate-100">{row.label}</td>
              {periodKeys.map((k) => (
                <td key={k} className="px-4 py-1 text-right tabular-nums text-[11px] font-semibold">{pctFmt(row.values[k])}</td>
              ))}
              <td className="px-4 py-1 text-right tabular-nums text-[11px] border-l border-slate-100">–</td>
            </tr>
          );
        }
        if (row.kind === 'total') {
          const t = total(row.values);
          const emphasize = row.key === 'cp1' || row.key === 'cp2' || row.key === 'gross-profit';
          return (
            <tr key={row.key} className={cn('font-semibold border-y border-slate-200', emphasize ? 'bg-emerald-50/60' : 'bg-slate-50')}>
              <td className={cn('sticky left-0 z-10 px-4 py-2 text-sm text-slate-900 border-r border-slate-200', emphasize ? 'bg-emerald-50/60' : 'bg-slate-50')}>{row.label}</td>
              {periodKeys.map((k) => (
                <td key={k} className="px-4 py-2 text-right tabular-nums text-sm">{fmt(row.values[k])}</td>
              ))}
              <td className="px-4 py-2 text-right tabular-nums text-sm border-l border-slate-200">{fmt(t)}</td>
            </tr>
          );
        }
        // kind 'line' — collapsible COGS / SG&A line
        const rowKey = `${buValue}:${row.key}`;
        const isOpen = !!expanded[rowKey];
        const details = row.details || [];
        const hasDetails = details.length > 0;
        const t = total(row.values);
        return (
          <React.Fragment key={row.key}>
            <tr
              className={cn(
                'text-muted-foreground transition-colors',
                hasDetails && 'cursor-pointer hover:bg-slate-50',
                !hasDetails && 'hover:bg-slate-50/50',
                isOpen && 'bg-slate-50'
              )}
              onClick={() => hasDetails && toggleRow(rowKey)}
            >
              <td className={cn('sticky left-0 z-10 px-4 py-1.5 pl-6 text-xs border-r border-slate-100', isOpen ? 'bg-slate-50' : 'bg-white')}>
                <span className="inline-flex items-center gap-1.5">
                  {hasDetails ? (
                    isOpen ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />
                  ) : (
                    <span className="inline-block w-3" />
                  )}
                  {row.label}
                  {hasDetails && <span className="ml-1 text-[9px] text-slate-400 font-medium">({details.length})</span>}
                </span>
              </td>
              {periodKeys.map((k) => (
                <td key={k} className="px-4 py-1.5 text-right tabular-nums text-xs">{row.values[k] ? fmt(row.values[k]) : '–'}</td>
              ))}
              <td className="px-4 py-1.5 text-right tabular-nums text-xs border-l border-slate-100">{t ? fmt(t) : '–'}</td>
            </tr>
            {isOpen && hasDetails && (
              <tr className="bg-slate-50/60">
                <td colSpan={periodKeys.length + 2} className="px-0 py-0 border-b border-slate-200">
                  <DetailPanel details={details} currency={currency} fmt={fmt} />
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const BusinessUnitPnLPage: React.FC = () => {
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(today), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(endOfMonth(today), 'yyyy-MM-dd'));
  const [viewPeriod, setViewPeriod] = useState<BUViewPeriod>('monthly');
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>('ETB');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleRow = (key: string) =>
    setExpanded((p) => ({ ...p, [key]: !p[key] }));
  const { data, isLoading, isFetching } = useBusinessUnitPnL(dateFrom, dateTo, viewPeriod, displayCurrency);

  const fmt = (n: number) => {
    if (!n) return '–';
    if (n < 0) return `(${formatCurrency(Math.abs(n), displayCurrency)})`;
    return formatCurrency(n, displayCurrency);
  };

  const handleExport = (detailed = false) => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const headerRow = [
      'Line Item',
      ...data.periodLabels.map((p) => p.label),
      'Total',
      ...(detailed ? ['BU %'] : []),
    ];

    for (const bu of BUSINESS_UNITS) {
      const cells = data.byBU[bu.value as BusinessUnit];
      const periodKeys = data.periodKeys;
      const allCatIds = Array.from(
        new Set(periodKeys.flatMap((k) => Object.keys(cells[k].opexByCategory)))
      );
      const sumCol = (pick: (k: string) => number) =>
        periodKeys.reduce((s, k) => s + pick(k), 0);

      const rows: (string | number)[][] = [headerRow];
      // Outline levels per sheet row (index 0 = header). Detail rows are level 1
      // and start collapsed so each line item can be expanded in Excel.
      const rowMeta: { level: number; hidden: boolean }[] = [{ level: 0, hidden: false }];
      const pushRow = (label: string, pick: (k: string) => number) => {
        const vals = periodKeys.map((k) => Math.round(pick(k)));
        rows.push([label, ...vals, Math.round(sumCol(pick))]);
        rowMeta.push({ level: 0, hidden: false });
      };
      const pushPlain = (cells0: (string | number)[]) => {
        rows.push(cells0);
        rowMeta.push({ level: 0, hidden: false });
      };
      // Inline, collapsible transaction rows directly under their line item.
      const pushDetails = (details: ExpenseDetail[]) => {
        for (const d of details.slice().sort((a, b) => (a.date < b.date ? 1 : -1))) {
          const parts = [
            d.date,
            d.vendor || '',
            d.description || '',
            d.costPosition || d.reference || '',
          ].filter(Boolean);
          const orig = `${d.currency} ${(Math.round(d.originalAmount * 100) / 100).toLocaleString()}`;
          const pct = d.pct == null ? 100 : d.pct;
          rows.push([
            `      ${parts.join(' · ')}  [${orig} · ${bu.label} ${pct}%]`,
            ...periodKeys.map(() => ''),
            Math.round(d.portion * 100) / 100,
            `${pct}%`,
          ]);
          rowMeta.push({ level: 1, hidden: true });
        }
      };


      if (bu.value === 'chipchip') {
        // ChipChip: the accrual-P&L layout (matches the on-screen section).
        for (const row of data.chipchipPnl) {
          if (row.kind === 'header') {
            pushPlain([row.label]);
          } else if (row.kind === 'margin') {
            pushPlain([
              `  ${row.label}`,
              ...periodKeys.map((k) => (Number.isFinite(row.values[k]) ? `${(row.values[k] * 100).toFixed(1)}%` : '–')),
              '',
            ]);
          } else {
            const indent = row.kind === 'sub' || row.kind === 'line' ? '  ' : '';
            pushRow(`${indent}${row.label}`, (k) => row.values[k] || 0);
            if (detailed && row.details?.length) {
              pushDetails(row.details);
            }
          }
        }
      } else {
        pushRow('Sales', (k) => cells[k].sales);
        if (bu.value === 'grantt' && sumCol((k) => cells[k].grantIncome || 0) > 0) {
          pushRow('  Grant Income', (k) => cells[k].grantIncome || 0);
          if (detailed) {
            pushDetails(periodKeys.flatMap((k) => cells[k].grantIncomeDetails || []));
          }
        }
        for (const catId of allCatIds) {
          const catName = data.categoryNames[catId] || 'Uncategorized';
          pushRow(`  ${catName}`, (k) => -(cells[k].opexByCategory[catId] || 0));
          if (detailed) {
            pushDetails(periodKeys.flatMap((k) => cells[k].opexDetailsByCategory?.[catId] || []));
          }
        }
      }
      pushRow('Total OpEx', (k) => -cells[k].opexTotal);
      if (sumCol((k) => cells[k].inflows) > 0) {
        pushRow('Cash Inflows', (k) => cells[k].inflows);
      }
      pushRow(
        'Net',
        (k) => cells[k].sales - cells[k].opexTotal + cells[k].inflows
      );

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [
        { wch: detailed ? 70 : 32 },
        ...periodKeys.map(() => ({ wch: 14 })),
        { wch: 16 },
        ...(detailed ? [{ wch: 8 }] : []),
      ];
      if (detailed) {
        ws['!rows'] = rowMeta.map((m) =>
          m.level ? { level: m.level, hidden: m.hidden } : {}
        );
        // Show the expand/collapse buttons below the group's summary row.
        (ws as any)['!outline'] = { above: true, left: false };
      }
      XLSX.utils.book_append_sheet(wb, ws, bu.label.slice(0, 31));
    }

    XLSX.writeFile(
      wb,
      `BU-PnL${detailed ? '-Detailed' : ''}_${dateFrom}_to_${dateTo}_${viewPeriod}.xlsx`
    );
  };



  return (
    <Layout>
      <div className="space-y-6 pb-12 animate-in fade-in duration-700">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="h-8 w-1 bg-primary rounded-full" />
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Business Unit P&amp;L
              </h1>
            </div>
            <p className="text-muted-foreground font-medium flex items-center gap-2 flex-wrap">
              <Badge
                variant="secondary"
                className="bg-slate-100 hover:bg-slate-200 text-slate-600 border-none px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider"
              >
                BU Pro
              </Badge>
              <Badge
                variant="outline"
                className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider"
                title="Cash basis: only paid expenses, booked on their payment date, and only this business unit's allocated share. The P&L Report is accrual, company-wide and booked on expense date."
              >
                Cash basis · payment date · BU allocation
              </Badge>
              {isValid(parseISO(dateFrom)) ? format(parseISO(dateFrom), 'MMM d') : '—'} —{' '}
              {isValid(parseISO(dateTo)) ? format(parseISO(dateTo), 'MMM d, yyyy') : '—'}
            </p>

          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Card className="flex items-center gap-1.5 p-1 bg-slate-50 border rounded-lg shadow-sm">
              <div className="flex items-center gap-2 px-2 border-r pr-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className="h-8 px-2 text-xs font-semibold gap-1.5 hover:bg-white">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {isValid(parseISO(dateFrom)) ? format(parseISO(dateFrom), 'MMM d, yyyy') : 'Start date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={isValid(parseISO(dateFrom)) ? parseISO(dateFrom) : undefined}
                      onSelect={(d) => d && setDateFrom(format(d, 'yyyy-MM-dd'))}
                      initialFocus
                      className={cn('p-3 pointer-events-auto')}
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground">—</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className="h-8 px-2 text-xs font-semibold gap-1.5 hover:bg-white">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {isValid(parseISO(dateTo)) ? format(parseISO(dateTo), 'MMM d, yyyy') : 'End date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={isValid(parseISO(dateTo)) ? parseISO(dateTo) : undefined}
                      onSelect={(d) => d && setDateTo(format(d, 'yyyy-MM-dd'))}
                      initialFocus
                      className={cn('p-3 pointer-events-auto')}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <Select value={viewPeriod} onValueChange={(v) => setViewPeriod(v as BUViewPeriod)}>
                <SelectTrigger className="w-32 h-8 border-none bg-transparent hover:bg-white shadow-none text-xs font-semibold">
                  <Activity className="h-3.5 w-3.5 mr-2 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>

              <div className="h-5 w-px bg-slate-200 mx-1" />

              <div className="flex p-0.5 gap-0.5">
                {(['ETB', 'USD'] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={cn(
                      'px-3 py-1 rounded-md text-[10px] font-bold transition-all',
                      displayCurrency === c
                        ? 'bg-white text-primary shadow-sm ring-1 ring-slate-200'
                        : 'text-slate-500 hover:text-slate-900'
                    )}
                    onClick={() => setDisplayCurrency(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Card>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!data} className="h-10 gap-1.5">
                {isFetching ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Export
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => handleExport(false)}>
                Summary (one sheet per BU)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport(true)}>
                Detailed (collapsible rows)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

        </div>


        {isLoading || !data ? (
          <Skeleton className="h-96 w-full" />
        ) : (
          <div className={cn('space-y-6', isFetching && 'opacity-70 transition-opacity')}>
            {BUSINESS_UNITS.map((bu) => {
              const cells = data.byBU[bu.value as BusinessUnit];
              const periodKeys = data.periodKeys;
              const allCatIds = Array.from(
                new Set(periodKeys.flatMap((k) => Object.keys(cells[k].opexByCategory)))
              );
              const totals = {
                sales: periodKeys.reduce((s, k) => s + cells[k].sales, 0),
                opex: periodKeys.reduce((s, k) => s + cells[k].opexTotal, 0),
                inflows: periodKeys.reduce((s, k) => s + cells[k].inflows, 0),
              };
              const net = totals.sales - totals.opex + totals.inflows;

              return (
                <Card key={bu.value} className="border-none shadow-lg overflow-hidden bg-white">
                  <CardHeader className="bg-slate-900 border-none pb-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-white text-base flex items-center gap-2">
                          <span style={{ color: bu.color }}>●</span> {bu.label}
                        </CardTitle>
                        <CardDescription className="text-slate-400 text-[10px]">
                          Sales · OpEx · Net
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={cn(
                            'text-[9px] uppercase font-black tracking-widest border',
                            net >= 0
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                          )}
                        >
                          Net: {fmt(net)}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="sticky left-0 z-20 bg-slate-50 w-64 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                              Line Item
                            </th>
                            {data.periodLabels.map((p) => (
                              <th
                                key={p.key}
                                className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[110px]"
                              >
                                {p.label}
                              </th>
                            ))}
                            <th className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px] border-l border-slate-200">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {bu.value === 'chipchip' && (
                            <ChipChipPnlRows
                              rows={data.chipchipPnl}
                              buValue={bu.value}
                              periodKeys={periodKeys}
                              expanded={expanded}
                              toggleRow={toggleRow}
                              currency={displayCurrency}
                              fmt={fmt}
                            />
                          )}
                          {bu.value !== 'chipchip' && (
                          <>
                          <tr className="hover:bg-slate-50/50">
                            <td className="sticky left-0 z-10 bg-white px-4 py-2 text-sm font-semibold text-slate-900 border-r border-slate-100">
                              Sales
                            </td>
                            {periodKeys.map((k) => (
                              <td key={k} className="px-4 py-2 text-right tabular-nums text-sm">
                                {fmt(cells[k].sales)}
                              </td>
                            ))}
                            <td className="px-4 py-2 text-right tabular-nums text-sm font-semibold border-l border-slate-100">
                              {fmt(totals.sales)}
                            </td>
                          </tr>
                          {bu.value === 'grantt' &&
                            periodKeys.reduce((s, k) => s + (cells[k].grantIncome || 0), 0) > 0 &&
                            (() => {
                              const giKey = `${bu.value}:grant-income`;
                              const giOpen = !!expanded[giKey];
                              const giDetails = periodKeys.flatMap((k) => cells[k].grantIncomeDetails || []);
                              const giTotal = periodKeys.reduce((s, k) => s + (cells[k].grantIncome || 0), 0);
                              return (
                                <React.Fragment>
                                  <tr
                                    className={cn(
                                      'text-muted-foreground transition-colors cursor-pointer hover:bg-slate-50',
                                      giOpen && 'bg-slate-50'
                                    )}
                                    onClick={() => toggleRow(giKey)}
                                  >
                                    <td className={cn('sticky left-0 z-10 pl-8 pr-4 py-1.5 text-xs border-r border-slate-100', giOpen ? 'bg-slate-50' : 'bg-white')}>
                                      <span className="inline-flex items-center gap-1.5">
                                        {giOpen ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
                                        Grant Income
                                        <span className="ml-1 text-[9px] text-slate-400 font-medium">({giDetails.length})</span>
                                      </span>
                                    </td>
                                    {periodKeys.map((k) => (
                                      <td key={k} className="px-4 py-1.5 text-right tabular-nums text-xs">
                                        {cells[k].grantIncome ? fmt(cells[k].grantIncome) : '–'}
                                      </td>
                                    ))}
                                    <td className="px-4 py-1.5 text-right tabular-nums text-xs border-l border-slate-100">{fmt(giTotal)}</td>
                                  </tr>
                                  {giOpen && giDetails.length > 0 && (
                                    <tr className="bg-slate-50/60">
                                      <td colSpan={periodKeys.length + 2} className="px-0 py-0 border-b border-slate-200">
                                        <DetailPanel details={giDetails} currency={displayCurrency} fmt={fmt} />
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })()}

                          <tr className="bg-slate-100/70 border-y border-slate-200">
                            <td className="sticky left-0 z-10 bg-slate-100/70 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 border-r border-slate-200">
                              OpEx
                            </td>
                            {periodKeys.map((k) => (
                              <td key={k} className="px-4 py-1.5" />
                            ))}
                            <td className="px-4 py-1.5 border-l border-slate-200" />
                          </tr>


                          {allCatIds.map((catId) => {
                            const rowKey = `${bu.value}:${catId}`;
                            const isOpen = !!expanded[rowKey];
                            const catTotal = periodKeys.reduce(
                              (s, k) => s + (cells[k].opexByCategory[catId] || 0),
                              0
                            );
                            // Collect details across selected periods
                            const details = periodKeys.flatMap((k) =>
                              (cells[k].opexDetailsByCategory[catId] || []).map((d) => ({
                                ...d,
                                periodKey: k,
                              }))
                            );
                            const hasDetails = details.length > 0;
                            return (
                              <React.Fragment key={catId}>
                                <tr
                                  className={cn(
                                    'text-muted-foreground transition-colors',
                                    hasDetails && 'cursor-pointer hover:bg-slate-50',
                                    !hasDetails && 'hover:bg-slate-50/50',
                                    isOpen && 'bg-slate-50'
                                  )}
                                  onClick={() => hasDetails && toggleRow(rowKey)}
                                >
                                  <td
                                    className={cn(
                                      'sticky left-0 z-10 px-4 py-1.5 pl-6 text-xs border-r border-slate-100',
                                      isOpen ? 'bg-slate-50' : 'bg-white'
                                    )}
                                  >
                                    <span className="inline-flex items-center gap-1.5">
                                      {hasDetails ? (
                                        isOpen ? (
                                          <ChevronDown className="h-3 w-3 text-slate-400" />
                                        ) : (
                                          <ChevronRight className="h-3 w-3 text-slate-400" />
                                        )
                                      ) : (
                                        <span className="inline-block w-3" />
                                      )}
                                      {data.categoryNames[catId] || 'Uncategorized'}
                                      {hasDetails && (
                                        <span className="ml-1 text-[9px] text-slate-400 font-medium">
                                          ({details.length})
                                        </span>
                                      )}
                                    </span>
                                  </td>
                                  {periodKeys.map((k) => {
                                    const v = cells[k].opexByCategory[catId] || 0;
                                    return (
                                      <td key={k} className="px-4 py-1.5 text-right tabular-nums text-xs">
                                        {v ? `(${fmt(v).replace(/[()]/g, '')})` : '–'}
                                      </td>
                                    );
                                  })}
                                  <td className="px-4 py-1.5 text-right tabular-nums text-xs border-l border-slate-100">
                                    {catTotal ? `(${fmt(catTotal).replace(/[()]/g, '')})` : '–'}
                                  </td>
                                </tr>

                                {isOpen && hasDetails && (
                                  <tr className="bg-slate-50/60">
                                    <td
                                      colSpan={periodKeys.length + 2}
                                      className="px-0 py-0 border-b border-slate-200"
                                    >
                                      <div className="px-8 py-3">
                                        <table className="w-full text-[11px]">
                                          <thead>
                                            <tr className="text-slate-500 uppercase text-[9px] tracking-wider">
                                              <th className="text-left py-1 pr-3 font-semibold">Date</th>
                                              <th className="text-left py-1 pr-3 font-semibold">Vendor</th>
                                              <th className="text-left py-1 pr-3 font-semibold">Description</th>
                                              <th className="text-left py-1 pr-3 font-semibold">Cost Position</th>
                                              <th className="text-right py-1 pr-3 font-semibold">Original</th>
                                              <th className="text-right py-1 pr-3 font-semibold">BU %</th>
                                              <th className="text-right py-1 font-semibold">
                                                {displayCurrency} Portion
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {details
                                              .slice()
                                              .sort((a, b) => (a.date < b.date ? 1 : -1))
                                              .map((d, idx) => (
                                                <tr
                                                  key={`${d.id}-${idx}`}
                                                  className="border-t border-slate-200/70 hover:bg-white/60"
                                                >
                                                  <td className="py-1 pr-3 text-slate-600 whitespace-nowrap">
                                                    {format(parseISO(d.date), 'MMM d, yyyy')}
                                                  </td>
                                                  <td className="py-1 pr-3 text-slate-700 truncate max-w-[180px]">
                                                    {d.vendor || '—'}
                                                  </td>
                                                  <td className="py-1 pr-3 text-slate-500 truncate max-w-[260px]">
                                                    {d.description || '—'}
                                                  </td>
                                                  <td className="py-1 pr-3 text-slate-500 truncate max-w-[180px]">
                                                    {d.costPosition || d.reference || '—'}
                                                  </td>
                                                  <td className="py-1 pr-3 text-right tabular-nums text-slate-600">
                                                    {formatCurrency(d.originalAmount, d.currency)}
                                                  </td>
                                                  <td className="py-1 pr-3 text-right tabular-nums text-slate-500">
                                                    {d.pct}%
                                                  </td>
                                                  <td className="py-1 text-right tabular-nums font-semibold text-slate-800">
                                                    {fmt(d.portion)}
                                                  </td>
                                                </tr>
                                              ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          </>
                          )}

                          <tr className="bg-slate-50 font-semibold border-y border-slate-200">
                            <td className="sticky left-0 z-10 bg-slate-50 px-4 py-2 text-sm text-slate-700 border-r border-slate-200">
                              Total OpEx
                            </td>
                            {periodKeys.map((k) => (
                              <td key={k} className="px-4 py-2 text-right tabular-nums text-sm">
                                {cells[k].opexTotal
                                  ? `(${fmt(cells[k].opexTotal).replace(/[()]/g, '')})`
                                  : '–'}
                              </td>
                            ))}
                            <td className="px-4 py-2 text-right tabular-nums text-sm border-l border-slate-200">
                              {totals.opex ? `(${fmt(totals.opex).replace(/[()]/g, '')})` : '–'}
                            </td>
                          </tr>

                          {totals.inflows > 0 && (
                            <tr className="hover:bg-slate-50/50 text-muted-foreground">
                              <td className="sticky left-0 z-10 bg-white px-4 py-2 text-sm border-r border-slate-100">
                                Cash Inflows
                              </td>
                              {periodKeys.map((k) => (
                                <td key={k} className="px-4 py-2 text-right tabular-nums text-sm">
                                  {fmt(cells[k].inflows)}
                                </td>
                              ))}
                              <td className="px-4 py-2 text-right tabular-nums text-sm border-l border-slate-100">
                                {fmt(totals.inflows)}
                              </td>
                            </tr>
                          )}

                          <tr className="bg-slate-900 text-white font-bold">
                            <td className="sticky left-0 z-10 bg-slate-900 px-4 py-3 text-sm uppercase tracking-wider border-r border-slate-700">
                              Net
                            </td>
                            {periodKeys.map((k) => {
                              const v =
                                cells[k].sales -
                                cells[k].opexTotal +
                                cells[k].inflows;
                              return (
                                <td
                                  key={k}
                                  className={cn(
                                    'px-4 py-3 text-right tabular-nums text-sm',
                                    v < 0 && 'text-rose-400'
                                  )}
                                >
                                  {fmt(v)}
                                </td>
                              );
                            })}
                            <td
                              className={cn(
                                'px-4 py-3 text-right tabular-nums text-sm border-l border-slate-700',
                                net < 0 && 'text-rose-400'
                              )}
                            >
                              {fmt(net)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BusinessUnitPnLPage;
